/**
 * peopleHubSlice.ts
 *
 * Redux state for the People Hub module — five tab data buckets plus the
 * active-session tracker for in-flight activities (Pulse Check, Full
 * Observation, Routine Observation).
 *
 * Naming / pattern: identical to observationSlice.ts / employeeLCWSlice.ts.
 * Async thunks live in peopleHubThunks.ts and are wired via extraReducers.
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createApiClient } from "../../api/apiClient";
import {
  fetchEmployeeProfile as fetchEmployeeProfileApi,
  setTaskCoverage as setTaskCoverageApi,
  reassignTask as reassignTaskApi,
} from "../../screens/PeopleHub/peopleHubApi";
import type {
  QueueItem,
  OnboardingEmployee,
  WorkloadView,
  WorkloadTimeframe,
  LocationWorkloadSnapshot,
  RecentActivityRecord,
  MyTeamMember,
  TrainingRecord,
  HistoryRecord,
  TeamMemberSummary,
} from "../../screens/PeopleHub/types";

// ─── Re-export for convenience (consumers can import from one place) ──────────
export type { QueueItem, MyTeamMember, TrainingRecord, HistoryRecord };

// ─── LoadingState helper ──────────────────────────────────────────────────────
export type LoadingState = "idle" | "loading" | "succeeded" | "failed";

// ─── Active-session descriptor ────────────────────────────────────────────────
// Tracks the overlay + in-flight activity session (set when the user clicks
// Proceed in BeginActivityOverlay).
export type PeopleHubActivityType =
  | "Pulse Check"
  | "Full Observation"
  | "Routine Observation";

export interface ActiveActivitySession {
  /** Redux-visible unique key (e.g. Date.now().toString()) */
  sessionId: string;
  employeeId: string;
  employeeName: string;
  role: string;
  /** "Tenured" | "New Hire" */
  employeeProfile: string;
  managerName: string;
  activityType: PeopleHubActivityType;
  /** ISO timestamp when the session was opened */
  startedAt: string;
  /** "new" → "in-progress" → "submitted" */
  status: "new" | "in-progress" | "submitted";
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab 1 — Home
// ─────────────────────────────────────────────────────────────────────────────
export interface CoverageBannerState {
  active: boolean;
  coveredBy: string;
  coveredByName: string;
  coverageFrom: string;
  coverageTo: string;
  offeredCount: number;
  acceptedCount: number;
  taskIds?: string[];
}

export interface HomeTabState {
  queueItems: QueueItem[];
  onboardingEmployees: OnboardingEmployee[];
  workloadSnapshot: LocationWorkloadSnapshot | null;
  workloadView: WorkloadView;
  workloadTimeframe: WorkloadTimeframe;
  recentActivity: RecentActivityRecord[];
  coverage: CoverageBannerState | null;
  coverageLoading: LoadingState;
  coverageError: string | null;
  loadingState: LoadingState;
  error: string | null;
}

const homeInitial: HomeTabState = {
  queueItems: [],
  onboardingEmployees: [],
  workloadSnapshot: null,
  workloadView: "site",
  workloadTimeframe: "cw",
  recentActivity: [],
  coverage: null,
  coverageLoading: "idle",
  coverageError: null,
  loadingState: "idle",
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 2 — My Team
// ─────────────────────────────────────────────────────────────────────────────
export interface MyTeamTabState {
  members: MyTeamMember[];
  /** Free-text search applied in the component */
  searchQuery: string;
  /** Active filter values (matches CrossPlatformDropdown pattern) */
  filters: {
    status: string;
    role: string;
    department: string;
  };
  loadingState: LoadingState;
  error: string | null;
}

const myTeamInitial: MyTeamTabState = {
  members: [],
  searchQuery: "",
  filters: {
    status: "",
    role: "",
    department: "",
  },
  loadingState: "idle",
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 3 — Coaching History
// ─────────────────────────────────────────────────────────────────────────────
export interface CoachingHistoryTabState {
  summaries: TeamMemberSummary[];
  filters: {
    employeeId: string;
    status: string;
    dateFrom: string;
    dateTo: string;
  };
  loadingState: LoadingState;
  error: string | null;
}

const coachingHistoryInitial: CoachingHistoryTabState = {
  summaries: [],
  filters: {
    employeeId: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  },
  loadingState: "idle",
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 4 — All History
// ─────────────────────────────────────────────────────────────────────────────
export interface AllHistoryTabState {
  records: HistoryRecord[];
  filters: {
    employeeId: string;
    type: string;
    dateFrom: string;
    dateTo: string;
  };
  loadingState: LoadingState;
  error: string | null;
}

const allHistoryInitial: AllHistoryTabState = {
  records: [],
  filters: {
    employeeId: "",
    type: "",
    dateFrom: "",
    dateTo: "",
  },
  loadingState: "idle",
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab 5 — Reports / Training
// ─────────────────────────────────────────────────────────────────────────────
export interface ReportsTabState {
  trainingRecords: TrainingRecord[];
  filters: {
    employeeId: string;
    status: string;
  };
  loadingState: LoadingState;
  error: string | null;
}

const reportsInitial: ReportsTabState = {
  trainingRecords: [],
  filters: {
    employeeId: "",
    status: "",
  },
  loadingState: "idle",
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Employee Profile — GET /people/employees/{employeeId}
// ─────────────────────────────────────────────────────────────────────────────
export interface EmployeeProfileTabState {
  data: Record<string, any> | null;
  loadingState: LoadingState;
  error: string | null;
}

const employeeProfileInitial: EmployeeProfileTabState = {
  data: null,
  loadingState: "idle",
  error: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// GET /people/activities cache — shared by the Home tab's Recent People Activity
// card and the All History tab, so navigating Home -> All History with the same
// default query doesn't re-trigger the API call.
// ─────────────────────────────────────────────────────────────────────────────
export interface ActivityHistoryQuery {
  locationName: string;
  search: string;
  sortBy: string;
  sortOrder: "ASC" | "DESC";
  page: number;
  size: number;
  /** Column -> selected raw filter values (e.g. { type: ["OBSERVATION"] }) — serialized for cache-key comparison. */
  filters?: Record<string, string[]>;
}

export interface ActivityHistoryCacheState {
  query: ActivityHistoryQuery | null;
  rows: Record<string, any>[];
  total: number;
  loadingState: LoadingState;
}

const activityHistoryInitial: ActivityHistoryCacheState = {
  query: null,
  rows: [],
  total: 0,
  loadingState: "idle",
};

// ─────────────────────────────────────────────────────────────────────────────
// Root state
// ─────────────────────────────────────────────────────────────────────────────
export interface PeopleHubState {
  home: HomeTabState;
  myTeam: MyTeamTabState;
  coachingHistory: CoachingHistoryTabState;
  allHistory: AllHistoryTabState;
  reports: ReportsTabState;
  employeeProfile: EmployeeProfileTabState;
  /** Cached GET /people/activities response (see ActivityHistoryCacheState). */
  activityHistory: ActivityHistoryCacheState;
  /** Currently open activity session (null = none open). */
  activeSession: ActiveActivitySession | null;
  /** Whether the Begin Activity overlay is visible. */
  isBeginActivityVisible: boolean;
}

const initialState: PeopleHubState = {
  home: homeInitial,
  myTeam: myTeamInitial,
  coachingHistory: coachingHistoryInitial,
  allHistory: allHistoryInitial,
  reports: reportsInitial,
  employeeProfile: employeeProfileInitial,
  activityHistory: activityHistoryInitial,
  activeSession: null,
  isBeginActivityVisible: false,
};

// ─────────────────────────────────────────────────────────────────────────────
// Async thunks
// ─────────────────────────────────────────────────────────────────────────────

// Tab 1 — Home: queue + workload + recent activity
export const fetchHomeData = createAsyncThunk(
  "peopleHub/fetchHomeData",
  async (locationName: string, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const [queueRes, recentRes] = await Promise.all([
        api.get<QueueItem[]>("/people-hub/queue", {
          params: { locationName },
        }),
        api.get<RecentActivityRecord[]>("/people-hub/recent-activity", {
          params: { locationName },
        }),
      ]);
      return {
        queueItems: queueRes.data,
        recentActivity: recentRes.data,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load home data"
      );
    }
  }
);

// Tab 1 — Home: workload snapshot (separate call — driven by view/timeframe toggles)
export const fetchWorkloadSnapshot = createAsyncThunk(
  "peopleHub/fetchWorkloadSnapshot",
  async (
    payload: { locationName: string; view: WorkloadView; timeframe: WorkloadTimeframe },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.get<LocationWorkloadSnapshot>(
        "/people-hub/workload-snapshot",
        { params: { locationName: payload.locationName, view: payload.view, timeframe: payload.timeframe } }
      );
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load workload data"
      );
    }
  }
);

// Tab 2 — My Team
export const fetchMyTeam = createAsyncThunk(
  "peopleHub/fetchMyTeam",
  async (locationName: string, { rejectWithValue }) => {
    try {
      const api = createApiClient();
      const res = await api.get<MyTeamMember[]>("/people-hub/my-team", {
        params: { locationName },
      });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load team"
      );
    }
  }
);

// Tab 3 — Coaching History
export const fetchCoachingHistory = createAsyncThunk(
  "peopleHub/fetchCoachingHistory",
  async (
    payload: { locationName: string; employeeId?: string; status?: string; dateFrom?: string; dateTo?: string },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.get<TeamMemberSummary[]>("/people-hub/coaching-history", {
        params: {
          locationName: payload.locationName,
          ...(payload.employeeId ? { employeeId: payload.employeeId } : {}),
          ...(payload.status ? { status: payload.status } : {}),
          ...(payload.dateFrom ? { dateFrom: payload.dateFrom } : {}),
          ...(payload.dateTo ? { dateTo: payload.dateTo } : {}),
        },
      });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load coaching history"
      );
    }
  }
);

// Tab 4 — All History
export const fetchAllHistory = createAsyncThunk(
  "peopleHub/fetchAllHistory",
  async (
    payload: { locationName: string; employeeId?: string; type?: string; dateFrom?: string; dateTo?: string },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.get<HistoryRecord[]>("/people-hub/history", {
        params: {
          locationName: payload.locationName,
          ...(payload.employeeId ? { employeeId: payload.employeeId } : {}),
          ...(payload.type ? { type: payload.type } : {}),
          ...(payload.dateFrom ? { dateFrom: payload.dateFrom } : {}),
          ...(payload.dateTo ? { dateTo: payload.dateTo } : {}),
        },
      });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load history"
      );
    }
  }
);

// Tab 5 — Reports / Training
export const fetchTrainingRecords = createAsyncThunk(
  "peopleHub/fetchTrainingRecords",
  async (
    payload: { locationName: string; employeeId?: string; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.get<TrainingRecord[]>("/people-hub/training", {
        params: {
          locationName: payload.locationName,
          ...(payload.employeeId ? { employeeId: payload.employeeId } : {}),
          ...(payload.status ? { status: payload.status } : {}),
        },
      });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load training records"
      );
    }
  }
);

// Employee Profile
export const fetchEmployeeProfileData = createAsyncThunk(
  "peopleHub/fetchEmployeeProfileData",
  async (
    payload: { employeeId: string; clientTimestamp: string; locationName?: string },
    { rejectWithValue }
  ) => {
    try {
      return await fetchEmployeeProfileApi(payload.employeeId, payload.clientTimestamp, payload.locationName);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to load employee profile"
      );
    }
  }
);

// Tab 1 — Home: Set Coverage Period (POST /api/people/cadence/coverage)
export const setCoverageThunk = createAsyncThunk(
  "peopleHub/setCoverageThunk",
  async (
    payload: {
      locationName: string;
      originalLeaderGpid: string;
      coveredByGpid: string;
      coverFrom: string;
      coverTo: string;
      coveredBy: string;
      coveredByName: string;
      taskIds: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await setTaskCoverageApi({
        locationName: payload.locationName,
        originalLeaderGpid: payload.originalLeaderGpid,
        coveredByGpid: payload.coveredByGpid,
        coverFrom: payload.coverFrom,
        coverTo: payload.coverTo,
      });
      return {
        ...data,
        coveredByName: payload.coveredByName,
        taskIds: payload.taskIds,
        coverFrom: payload.coverFrom,
        coverTo: payload.coverTo,
        coveredBy: payload.coveredBy,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to set coverage"
      );
    }
  }
);

// Tab 1 — Home: Reassign Task (POST /api/people/cadence/tasks/{taskId}/reassign)
export const reassignTaskThunk = createAsyncThunk(
  "peopleHub/reassignTaskThunk",
  async (
    payload: {
      taskId: string;
      actingUserGpid: string;
      reassignToGpid: string;
      reassignTo: string;
      reassignToName: string;
      reason: string;
      reasonOther?: string;
      isDirectReassign?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await reassignTaskApi(payload.taskId, {
        actingUserGpid: payload.actingUserGpid,
        reassignToGpid: payload.reassignToGpid,
        // "Other" carries its free-text explanation in reasonOther.
        reason: payload.reasonOther || payload.reason,
      });
      return {
        ...data,
        taskId: payload.taskId,
        reassignTo: payload.reassignTo,
        reassignToName: payload.reassignToName,
        reason: payload.reason,
        reasonOther: payload.reasonOther,
        isDirectReassign: payload.isDirectReassign,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to reassign task"
      );
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────
const peopleHubSlice = createSlice({
  name: "peopleHub",
  initialState,
  reducers: {
    // ── Begin Activity overlay ────────────────────────────────────────────
    setBeginActivityVisible(state, action: PayloadAction<boolean>) {
      state.isBeginActivityVisible = action.payload;
    },

    // ── Session lifecycle ─────────────────────────────────────────────────
    /** Open a new activity session (called when the user clicks Proceed). */
    startActivitySession(state, action: PayloadAction<Omit<ActiveActivitySession, "sessionId" | "startedAt" | "status">>) {
      state.activeSession = {
        ...action.payload,
        sessionId: Date.now().toString(),
        startedAt: new Date().toISOString(),
        status: "new",
      };
      state.isBeginActivityVisible = false;
    },
    /** Mark the session as in-progress (first edit after Proceed). */
    markSessionInProgress(state) {
      if (state.activeSession) {
        state.activeSession.status = "in-progress";
      }
    },
    /** Clear the session on Exit or after successful submission. */
    clearActivitySession(state) {
      state.activeSession = null;
    },

    // ── Tab 1 — Home ──────────────────────────────────────────────────────
    setWorkloadView(state, action: PayloadAction<WorkloadView>) {
      state.home.workloadView = action.payload;
    },
    setWorkloadTimeframe(state, action: PayloadAction<WorkloadTimeframe>) {
      state.home.workloadTimeframe = action.payload;
    },
    setWorkloadSnapshot(state, action: PayloadAction<LocationWorkloadSnapshot | null>) {
      state.home.workloadSnapshot = action.payload;
    },
    setOnboardingEmployees(state, action: PayloadAction<OnboardingEmployee[]>) {
      state.home.onboardingEmployees = action.payload;
    },
    setCoverageAction(state, action: PayloadAction<CoverageBannerState | null>) {
      state.home.coverage = action.payload;
    },
    acceptCoverageAction(state) {
      if (state.home.coverage) {
        state.home.coverage.acceptedCount = state.home.coverage.offeredCount;
        state.home.coverage.offeredCount = 0;
      }
    },
    endCoverageAction(state) {
      state.home.coverage = null;
    },

    // ── Tab 2 — My Team ───────────────────────────────────────────────────
    setMyTeamSearchQuery(state, action: PayloadAction<string>) {
      state.myTeam.searchQuery = action.payload;
    },
    setMyTeamFilters(
      state,
      action: PayloadAction<Partial<MyTeamTabState["filters"]>>
    ) {
      state.myTeam.filters = { ...state.myTeam.filters, ...action.payload };
    },
    resetMyTeamFilters(state) {
      state.myTeam.filters = myTeamInitial.filters;
      state.myTeam.searchQuery = "";
    },

    // ── Tab 3 — Coaching History ──────────────────────────────────────────
    setCoachingHistoryFilters(
      state,
      action: PayloadAction<Partial<CoachingHistoryTabState["filters"]>>
    ) {
      state.coachingHistory.filters = {
        ...state.coachingHistory.filters,
        ...action.payload,
      };
    },
    resetCoachingHistoryFilters(state) {
      state.coachingHistory.filters = coachingHistoryInitial.filters;
    },

    // ── Tab 4 — All History ───────────────────────────────────────────────
    setAllHistoryFilters(
      state,
      action: PayloadAction<Partial<AllHistoryTabState["filters"]>>
    ) {
      state.allHistory.filters = {
        ...state.allHistory.filters,
        ...action.payload,
      };
    },
    resetAllHistoryFilters(state) {
      state.allHistory.filters = allHistoryInitial.filters;
    },

    // ── Tab 5 — Reports ───────────────────────────────────────────────────
    setReportsFilters(
      state,
      action: PayloadAction<Partial<ReportsTabState["filters"]>>
    ) {
      state.reports.filters = { ...state.reports.filters, ...action.payload };
    },
    resetReportsFilters(state) {
      state.reports.filters = reportsInitial.filters;
    },

    // ── Employee Profile ──────────────────────────────────────────────────
    /** Clears the loaded employee profile — called when leaving the Employee Profile screen. */
    resetEmployeeProfile(state) {
      state.employeeProfile = employeeProfileInitial;
    },

    // ── Activity History cache (GET /people/activities) ──────────────────
    setActivityHistoryLoading(state) {
      state.activityHistory.loadingState = "loading";
    },
    setActivityHistoryData(
      state,
      action: PayloadAction<{ query: ActivityHistoryQuery; rows: Record<string, any>[]; total: number }>
    ) {
      state.activityHistory = {
        query: action.payload.query,
        rows: action.payload.rows,
        total: action.payload.total,
        loadingState: "succeeded",
      };
    },

    // ── Full reset ────────────────────────────────────────────────────────
    resetPeopleHubState() {
      return initialState;
    },
  },

  // ─── extraReducers — async thunk lifecycle handlers ──────────────────────
  extraReducers: (builder) => {
    // ── fetchHomeData ────────────────────────────────────────────────────
    builder
      .addCase(fetchHomeData.pending, (state) => {
        state.home.loadingState = "loading";
        state.home.error = null;
      })
      .addCase(fetchHomeData.fulfilled, (state, action) => {
        state.home.loadingState = "succeeded";
        state.home.queueItems = action.payload.queueItems;
        state.home.recentActivity = action.payload.recentActivity;
      })
      .addCase(fetchHomeData.rejected, (state, action) => {
        state.home.loadingState = "failed";
        state.home.error = action.payload as string;
      })
      // ── setCoverageThunk ─────────────────────────────────────────────────
      .addCase(setCoverageThunk.pending, (state) => {
        state.home.coverageLoading = "loading";
        state.home.coverageError = null;
      })
      .addCase(setCoverageThunk.fulfilled, (state, action) => {
        state.home.coverageLoading = "succeeded";
        const result = action.payload as Record<string, any>;
        const banner = result.coverageBanner;
        state.home.coverage = {
          active: banner?.active ?? true,
          coveredBy: result.coveredBy,
          coveredByName: result.coveredByName,
          coverageFrom: result.coverFrom,
          coverageTo: result.coverTo,
          offeredCount: result.offeredCount ?? result.tasksCovered ?? result.taskIds?.length ?? 0,
          acceptedCount: result.acceptedCount ?? 0,
          taskIds: result.taskIds ?? [],
        };
      })
      .addCase(setCoverageThunk.rejected, (state, action) => {
        state.home.coverageLoading = "failed";
        state.home.coverageError = action.payload as string;
      });

    // ── fetchWorkloadSnapshot ────────────────────────────────────────────
    builder
      .addCase(fetchWorkloadSnapshot.pending, (state) => {
        state.home.error = null;
      })
      .addCase(fetchWorkloadSnapshot.fulfilled, (state, action) => {
        state.home.workloadSnapshot = action.payload;
      })
      .addCase(fetchWorkloadSnapshot.rejected, (state, action) => {
        state.home.error = action.payload as string;
      });

    // ── fetchMyTeam ──────────────────────────────────────────────────────
    builder
      .addCase(fetchMyTeam.pending, (state) => {
        state.myTeam.loadingState = "loading";
        state.myTeam.error = null;
      })
      .addCase(fetchMyTeam.fulfilled, (state, action) => {
        state.myTeam.loadingState = "succeeded";
        state.myTeam.members = action.payload;
      })
      .addCase(fetchMyTeam.rejected, (state, action) => {
        state.myTeam.loadingState = "failed";
        state.myTeam.error = action.payload as string;
      });

    // ── fetchCoachingHistory ─────────────────────────────────────────────
    builder
      .addCase(fetchCoachingHistory.pending, (state) => {
        state.coachingHistory.loadingState = "loading";
        state.coachingHistory.error = null;
      })
      .addCase(fetchCoachingHistory.fulfilled, (state, action) => {
        state.coachingHistory.loadingState = "succeeded";
        state.coachingHistory.summaries = action.payload;
      })
      .addCase(fetchCoachingHistory.rejected, (state, action) => {
        state.coachingHistory.loadingState = "failed";
        state.coachingHistory.error = action.payload as string;
      });

    // ── fetchAllHistory ──────────────────────────────────────────────────
    builder
      .addCase(fetchAllHistory.pending, (state) => {
        state.allHistory.loadingState = "loading";
        state.allHistory.error = null;
      })
      .addCase(fetchAllHistory.fulfilled, (state, action) => {
        state.allHistory.loadingState = "succeeded";
        state.allHistory.records = action.payload;
      })
      .addCase(fetchAllHistory.rejected, (state, action) => {
        state.allHistory.loadingState = "failed";
        state.allHistory.error = action.payload as string;
      });

    // ── fetchTrainingRecords ─────────────────────────────────────────────
    builder
      .addCase(fetchTrainingRecords.pending, (state) => {
        state.reports.loadingState = "loading";
        state.reports.error = null;
      })
      .addCase(fetchTrainingRecords.fulfilled, (state, action) => {
        state.reports.loadingState = "succeeded";
        state.reports.trainingRecords = action.payload;
      })
      .addCase(fetchTrainingRecords.rejected, (state, action) => {
        state.reports.loadingState = "failed";
        state.reports.error = action.payload as string;
      });

    // ── fetchEmployeeProfileData ─────────────────────────────────────────
    builder
      .addCase(fetchEmployeeProfileData.pending, (state) => {
        state.employeeProfile.loadingState = "loading";
        state.employeeProfile.error = null;
      })
      .addCase(fetchEmployeeProfileData.fulfilled, (state, action) => {
        state.employeeProfile.loadingState = "succeeded";
        state.employeeProfile.data = action.payload;
      })
      .addCase(fetchEmployeeProfileData.rejected, (state, action) => {
        state.employeeProfile.loadingState = "failed";
        state.employeeProfile.error = action.payload as string;
      });
  },
});

export const {
  // overlay
  setBeginActivityVisible,
  // session
  startActivitySession,
  markSessionInProgress,
  clearActivitySession,
  // home
  setWorkloadView,
  setWorkloadTimeframe,
  setWorkloadSnapshot,
  setOnboardingEmployees,
  setCoverageAction,
  acceptCoverageAction,
  endCoverageAction,
  // my team
  setMyTeamSearchQuery,
  setMyTeamFilters,
  resetMyTeamFilters,
  // coaching history
  setCoachingHistoryFilters,
  resetCoachingHistoryFilters,
  // all history
  setAllHistoryFilters,
  resetAllHistoryFilters,
  // reports
  setReportsFilters,
  resetReportsFilters,
  // activity history cache
  setActivityHistoryLoading,
  setActivityHistoryData,
  // employee profile
  resetEmployeeProfile,
  // root
  resetPeopleHubState,
} = peopleHubSlice.actions;

export default peopleHubSlice.reducer;
